import { Platform } from 'react-native';
import type { ViewStyle } from 'react-native';
import { DOCK_PANEL_TRANSITION_MS } from '../theme/layout';

/**
 * Extends ViewStyle with CSS properties that React Native Web supports at
 * runtime but are absent from the RN type definitions. Using this instead of
 * `as any` keeps the rest of the style object type-checked.
 */
export type WebViewStyle = ViewStyle & {
  overflow?: ViewStyle['overflow'] | 'auto';
  scrollbarWidth?: 'auto' | 'thin' | 'none';
  backdropFilter?: string;
  WebkitBackdropFilter?: string;
  boxShadow?: string;
  transitionProperty?: string;
  transitionDuration?: string;
  transitionTimingFunction?: string;
};

// RN Web only auto-applies cursor:pointer for a handful of roles, so
// interactive primitives spread these into their styles explicitly.
// Null on native — safe inside style arrays. ('not-allowed' is valid CSS
// that RN Web passes through but RN's CursorValue type doesn't list.)
export const CURSOR_POINTER: ViewStyle | null =
  Platform.OS === 'web' ? { cursor: 'pointer' } : null;
export const CURSOR_DISABLED: ViewStyle | null =
  Platform.OS === 'web' ? ({ cursor: 'not-allowed' } as unknown as ViewStyle) : null;

// Frosted-glass backdrop blur for chrome that floats over the map
// (MapPathsOverlay's card), paired with colors.surfaceTranslucent. Native
// has no backdrop-filter equivalent, so native call sites pair
// colors.surfaceTranslucentSolid with a plain (unblurred) card instead —
// null here is safe inside a style array. RNW forwards `backdropFilter`
// through its style pipeline as-is (no whitelist strips it — see
// createReactDOMStyle.js) and auto-prefixes it via inline-style-prefixer's
// prefixMap (`backdropFilter: ['Webkit']`), so the explicit
// `WebkitBackdropFilter` below is belt-and-suspenders, not load-bearing.
export const FROSTED_GLASS_BLUR: WebViewStyle | null =
  Platform.OS === 'web'
    ? { backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }
    : null;

// Eased state-change transitions for desktop-web chrome (hover/active swaps).
// Web-only; null on native so they're safe inside style arrays. Animating
// background + transform reads as crafted rather than an instant snap.
export const TRANSITION_HOVER: ViewStyle | null =
  Platform.OS === 'web'
    ? ({
        transitionProperty: 'background-color, border-color, transform',
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-out',
      } as unknown as ViewStyle)
    : null;
export const TRANSITION_TRANSFORM: ViewStyle | null =
  Platform.OS === 'web'
    ? ({
        transitionProperty: 'transform',
        transitionDuration: '150ms',
        transitionTimingFunction: 'ease-out',
      } as unknown as ViewStyle)
    : null;

// Floating map chrome anchored to either edge (search bar, MapControls on
// the right; the map key, the search bar again, the post hover card on the
// left since the defense plan editor/viewer started docking there in Sept
// 2026) shifts in step with a DockedPanel's own slide-in (DockedPanel.tsx's
// OPEN_MS + Easing.out(Easing.cubic)) rather than jump-cutting to its new
// position once a panel finishes opening. Both `left` and `right` are
// listed so the same constant animates either edge's chrome — a
// right-anchored element only ever changes `right`, a left-anchored one
// only `left`, so listing both costs nothing on either.
// cubic-bezier(0.33, 1, 0.68, 1) is the CSS equivalent of that RN easing
// curve (easings.net's "easeOutCubic").
export const TRANSITION_DOCK_INSET: ViewStyle | null =
  Platform.OS === 'web'
    ? ({
        transitionProperty: 'left, right',
        transitionDuration: `${DOCK_PANEL_TRANSITION_MS}ms`,
        transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
      } as unknown as ViewStyle)
    : null;

/**
 * Switch colors for the ink action palette.
 *
 * react-native-web's Switch ignores `thumbColor` while ON and falls back to
 * its own hard-coded `defaultActiveThumbColor` (#009688, Material teal) unless
 * you pass the web-only `activeThumbColor`. That teal is in no REDI palette,
 * and it survived every code grep because nothing in this repo ever wrote it.
 * Spread this into every Switch so the ON thumb is the theme's `actionOn`.
 */
/**
 * Opt a View out of creating a stacking context.
 *
 * Every react-native-web View ships `position: relative` AND `z-index: 0`
 * (View/index.js, `view$raw`), and a positioned element with a numeric z-index
 * creates a stacking context. Descendants are then capped by that ancestor
 * however high their own z-index goes: the map's hover card sits at 1100
 * inside a wrapper at 0, so it painted under the search overlay at 100.
 *
 * Raising the ancestor instead would lift the whole subtree — the map tiles
 * with it — so the fix is to stop trapping. Spread this into an ancestor whose
 * children need to compete at the level above. Note `opacity` below 1 makes a
 * stacking context too, and this cannot undo that.
 */
export const Z_INDEX_AUTO: ViewStyle | null =
  Platform.OS === 'web' ? ({ zIndex: 'auto' } as unknown as ViewStyle) : null;

/**
 * Resting row count for a `multiline` TextInput.
 *
 * react-native-web renders multiline as a `<textarea>` and forwards `rows` only
 * when you pass it (TextInput/index.js: `supportedProps.rows = multiline ? rows
 * ?? numberOfLines : 1`). With neither set, the browser's own default of two
 * rows fixes the resting height, so a one-line pill renders double height with
 * its placeholder pinned to the top. `numberOfLines` would work but RNW warns
 * that it's deprecated, and on Android it clamps the visible lines instead.
 *
 * RN's TextInputProps has no `rows`, hence the cast. Empty on native, where the
 * height comes from style.
 */
export function textInputRows(rows: number) {
  return Platform.OS === 'web' ? ({ rows } as { rows?: number }) : {};
}

export function switchColors(colors: { border: string; action: string; actionOn: string }) {
  return {
    trackColor: { false: colors.border, true: colors.action },
    thumbColor: colors.actionOn,
    ...(Platform.OS === 'web' ? { activeThumbColor: colors.actionOn } : {}),
  };
}
